import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Drawer, DrawerPosition, Heading } from '@savgroup-front-common/core';

import {
  $BackButton,
  $BackButtonWrap,
  $CloseButtonWrap,
  $DrawerHeader,
  $PathOption,
  $PathPlaceholder,
  $PathPlaceholderHint,
  $PathPlaceholderIntegration,
  $SelectedPathLabel,
  $SelectedPathStrip,
  $StepContent,
  $StepTitle,
} from './DossierCreateDrawer.styles';
import {
  DOSSIER_CREATE_PATH_TYPES,
  type DossierCreatePathType,
} from './dossierCreate.types';
import messages from './messages';

const DRAWER_WIDTH_MIN = 320;
const DRAWER_WIDTH_MAX = 480;

const getDrawerWidthPx = (): number => {
  if (typeof window === 'undefined') {
    return 420;
  }

  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, window.innerWidth));
};

const CREATE_PATHS: DossierCreatePathType[] = [
  DOSSIER_CREATE_PATH_TYPES.NUMERO_COMMANDE,
  DOSSIER_CREATE_PATH_TYPES.INFO_PRODUIT,
  DOSSIER_CREATE_PATH_TYPES.CODE_BARRES,
  DOSSIER_CREATE_PATH_TYPES.RECHERCHE_CLIENT,
];

const PATH_HINT_MESSAGE_IDS: Record<DossierCreatePathType, keyof typeof messages> = {
  [DOSSIER_CREATE_PATH_TYPES.NUMERO_COMMANDE]: 'pathHintNumeroCommande',
  [DOSSIER_CREATE_PATH_TYPES.INFO_PRODUIT]: 'pathHintInfoProduit',
  [DOSSIER_CREATE_PATH_TYPES.CODE_BARRES]: 'pathHintCodeBarres',
  [DOSSIER_CREATE_PATH_TYPES.RECHERCHE_CLIENT]: 'pathHintRechercheClient',
};

export interface DossierCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DossierCreateDrawer = ({ isOpen, onClose }: DossierCreateDrawerProps) => {
  const intl = useIntl();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedPath, setSelectedPath] = useState<DossierCreatePathType | undefined>();
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidthPx);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updateWidth = () => {
      setDrawerWidth(getDrawerWidthPx());
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setStep(1);
    setSelectedPath(undefined);
    onClose();
  }, [onClose]);

  const handleSelectPath = useCallback((path: DossierCreatePathType) => {
    setSelectedPath(path);
    setStep(2);
  }, []);

  const handleBack = useCallback(() => {
    setStep(1);
    setSelectedPath(undefined);
  }, []);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      position={DrawerPosition.Right}
      width={drawerWidth}
      dataTestId="dossier-create-drawer"
    >
      <$DrawerHeader>
        <Heading level={2}>{intl.formatMessage(messages.title)}</Heading>
        <$CloseButtonWrap>
          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={handleClose}
            dataTestId="dossier-create-drawer-close"
            ariaLabel={{ id: 'drawer.close', defaultMessage: 'Fermer' }}
          >
            ✕
          </Button>
        </$CloseButtonWrap>
      </$DrawerHeader>

      <$StepContent>
        {step === 1 && (
          <>
            <$StepTitle>{intl.formatMessage(messages.choosePath)}</$StepTitle>
            {CREATE_PATHS.map((path) => (
              <$PathOption
                key={path}
                type="button"
                onClick={() => handleSelectPath(path)}
                data-testid={`dossier-create-path-${path}`}
              >
                {intl.formatMessage(messages[path])}
              </$PathOption>
            ))}
          </>
        )}

        {step === 2 && selectedPath && (
          <>
            <$SelectedPathStrip>
              <$SelectedPathLabel>{intl.formatMessage(messages.selectedPath)}</$SelectedPathLabel>
              {intl.formatMessage(messages[selectedPath])}
            </$SelectedPathStrip>
            <$BackButtonWrap>
              <$BackButton type="button" onClick={handleBack} data-testid="dossier-create-drawer-back">
                {intl.formatMessage(messages.back)}
              </$BackButton>
            </$BackButtonWrap>
            <$PathPlaceholder data-testid={`dossier-create-path-placeholder-${selectedPath}`}>
              <$PathPlaceholderHint>
                {intl.formatMessage(messages[PATH_HINT_MESSAGE_IDS[selectedPath]])}
              </$PathPlaceholderHint>
              <$PathPlaceholderIntegration>
                {intl.formatMessage(messages.integrationHint)}
              </$PathPlaceholderIntegration>
            </$PathPlaceholder>
          </>
        )}
      </$StepContent>
    </Drawer>
  );
};

export default DossierCreateDrawer;
